import styled from "styled-components";
import { useRecoilValue } from "recoil";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBTabsPane,
} from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { useDecision } from "@optimizely/react-sdk";
import { errorState, useGameQuery } from "./Settings.state";
import { Alert, Refresher, SidebarLayout } from "../common";
import { Row } from "../App.styles";
import Mandatory from "./Mandatory";
import MandatoryWithMedia from "./MandatoryWithMedia";
import Optional from "./Optional";
import Paid from "./Paid";
import Payment from "./Payment";
import SettingsPlaceholder from "./SettingsPlaceholder";
import { withPlaceholder } from "../common";
import { friendlyMessages } from '../lib/error'
import AlertDisplay from "../common/AlertDisplay";
import { usePageListeners } from "../common/Refresher.state";
import ROUTE_PATTERNS from "../consts/routes";

const Tabs = styled(MDBTabs)`
  background-color: #eceff1;

  .nav-link {
    &.active {
      border-color: ${({ theme }) => theme.primaryColor};
      color: ${({ theme }) => theme.primaryColor};
    }
  }

  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    .nav-item {
      flex-grow: 1;
    }

    .nav-link {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
      text-align: center;
    }
  }
`;

const TabsContent = styled(MDBTabsContent)`
  margin-bottom: 2.5rem;
`;

const AlertRow = styled.div`
  scroll-margin: 100px;
`;

/**
 * The main Settings dashboard component.
 * Provides a tabbed interface for managing game configurations, including Mandatory,
 * Optional, and Payment settings. Integrates with Optimizely for feature toggles (e.g., `game-carousel`).
 * 
 * @returns {React.ReactElement} The rendered Settings dashboard.
 */
const Settings = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const history = useHistory();
  const { hash } = useLocation();
  const alert = useRef(null);
  const [section, setSection] = useState("");
  const errors = useRecoilValue(errorState(game_id));
  const useQuery = () => useGameQuery(game_id);
  const [params] = useQuery();
  const { game_already_paid } = params || {};
  const [decisionGameCarousel] = useDecision('game-carousel');

  useEffect(() => setSection(hash.slice(1)), [hash]);
  useEffect(() => {
    if (history && section) {
      const newHash = `#${section}`;

      if (hash !== newHash) {
        history.push(newHash);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const gameIds = useMemo(() => [parseInt(game_id)], [game_id])
  const listeners = usePageListeners(ROUTE_PATTERNS.GAME_EDIT, { gameIds })

  return (
    <>
      <AlertDisplay />
      <AlertRow ref={alert}>
        <Alert color="error">
          {!!errors.length && (
            <>
              {
                errors.map((error, id) => (
                    <span key={id} dangerouslySetInnerHTML={{ __html: `${friendlyMessages?.[error] || error}&nbsp;` }} />
                  ))
              }
            </>
          )}
        </Alert>
      </AlertRow>
      <Row>
        <SidebarLayout message={t("These settings apply to your game You can change them anytime, even during the game.")}>
          {
            !params || Object.keys(params).length === 0 ? null : (
              <>
                <Tabs className="mb-3">
                  <MDBTabsItem>
                    <MDBTabsLink
                      active={section !== "optional" && section !== "payment"}
                      onClick={() => setSection("mandatory")}
                    >
                      {t('Mandatory')}
                    </MDBTabsLink>
                  </MDBTabsItem>
                  <MDBTabsItem>
                    <MDBTabsLink
                      active={section === "optional"}
                      onClick={() => setSection("optional")}
                    >
                      {t('Optional')}
                    </MDBTabsLink>
                  </MDBTabsItem>
                  <MDBTabsItem>
                    <MDBTabsLink
                      active={section === "payment"}
                      onClick={() => setSection("payment")}
                    >
                      {t('Payment')}
                    </MDBTabsLink>
                  </MDBTabsItem>
                </Tabs>
                <TabsContent>
                  <MDBTabsPane show={section !== "optional" && section !== "payment"}>
                    {
                      decisionGameCarousel?.enabled
                        ? <MandatoryWithMedia useQuery={useQuery} />
                        : <Mandatory useQuery={useQuery} />
                    }
                  </MDBTabsPane>
                  <MDBTabsPane show={section === "optional"}>
                    <Optional useQuery={useQuery} />
                  </MDBTabsPane>
                  <MDBTabsPane show={section === "payment"}>
                    {game_already_paid ? (
                      <Paid useQuery={useQuery} />
                    ) : (
                      <Payment useQuery={useQuery} />
                    )}
                  </MDBTabsPane>
                </TabsContent>
              </>
            )
          }
        </SidebarLayout>
      </Row>
      <Refresher listeners={listeners} />
    </>
  );
};

export default withPlaceholder(Settings, SettingsPlaceholder);